// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import {useMutation} from '@apollo/react-hooks';

import type {CoreEntityType} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    DeleteSavedFilter as DeleteSavedFilterMutation,
    DeleteSavedFilterVariables as DeleteSavedFilterVariables,
    FilterDeleteInput,
    GetSavedFiltersVariables as GetSavedFiltersQueryVariables,
} from 'nutshell-graphql-types';

import {getSavedFiltersReportType} from '../../entity-list-sidebar/helpers';

import DELETE_SAVED_FILTER from '../mutations/delete-saved-filter.graphql';
import GET_SAVED_FILTERS from '../queries/get-saved-filters.graphql';

export const useDeleteSavedFilter = (
    entityType: CoreEntityType,
    searchNameQuery?: string | null
): {
    deleteSavedFilter: (arg1: FilterDeleteInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [deleteSavedFilter, {loading, error}] = useMutation<
        DeleteSavedFilterMutation,
        DeleteSavedFilterVariables
    >(DELETE_SAVED_FILTER);

    return {
        deleteSavedFilter: (input: FilterDeleteInput) =>
            deleteSavedFilter({
                variables: {input},
                update: (proxy: any, mutationResults) => {
                    const deletedFilter =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.filterDelete &&
                        mutationResults.data.filterDelete.filter;

                    if (!deletedFilter) return;

                    if (deletedFilter.isShared) {
                        const variables = {
                            type: getSavedFiltersReportType(entityType),
                            includeShared: true,
                            ignoreUserFilters: true,
                            searchNameQuery: searchNameQuery,
                        } as const;

                        updateSavedFilters(
                            deletedFilter.id,
                            variables,
                            searchNameQuery,
                            entityType,
                            proxy
                        );
                    } else {
                        const variables = {
                            type: getSavedFiltersReportType(entityType),
                            includeShared: false,
                            searchNameQuery: searchNameQuery,
                        } as const;

                        updateSavedFilters(
                            deletedFilter.id,
                            variables,
                            searchNameQuery,
                            entityType,
                            proxy
                        );
                    }
                },
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};

function updateSavedFilters(
    id: string,
    variables: GetSavedFiltersQueryVariables,
    searchNameQuery: string | null | undefined,
    entityType: CoreEntityType,
    proxy: DataProxy
) {
    const cachedResult = proxy.readQuery({
        query: GET_SAVED_FILTERS,
        variables,
    });

    if (!cachedResult) return;

    const {edges} = cachedResult.savedFilters;
    // @ts-expect-error
    const newEdges = edges.filter((edge) => edge.node.id !== id);

    proxy.writeQuery({
        query: GET_SAVED_FILTERS,
        variables,
        data: {
            savedFilters: {
                ...cachedResult.savedFilters,
                edges: newEdges,
            },
        },
    });
}
